import { rd, type RemoteData } from '@passionware/monads';

type Otherwise<T> = {
  otherwise<U>(fallback: U): T | U;
};

type Probe = (...args: never[]) => boolean;

type IfCanChain<TCan> = {
  render<T>(renderer: (can: TCan) => T): Otherwise<T>;
};

type IfCanFn = {
  <TCan extends Probe>(can: TCan): IfCanChain<TCan>;
  ready: <TCanRd extends (...args: never[]) => RemoteData<boolean>>(
    canRd: TCanRd
  ) => IfCanChain<(...args: Parameters<TCanRd>) => boolean>;
  /**
   * Observe-only parent: never grants from its own probe. Keep the render
   * outcome when any {@link ifCan.childOf} child grants.
   */
  union: () => IfCanChain<() => boolean>;
  /**
   * Nest under an intercepted parent from {@link ifCan.union} (or another
   * `ifCan` render). Same-probe nesting does not need this — wrap the
   * intercepted `can` with `ifCan(can)` instead.
   */
  childOf: (parentCan: Probe) => IfCanFn;
};

const notifyParentKey = Symbol('ifCan.notifyParent');

type CanWithNotify<TCan> = TCan & {
  [notifyParentKey]?: () => void;
};

function getNotify(can: Probe): (() => void) | undefined {
  return (can as CanWithNotify<Probe>)[notifyParentKey];
}

function createChain<TCan extends Probe>(
  probe: TCan,
  notifyParent?: () => void
): IfCanChain<TCan> {
  return {
    render<T>(renderer: (can: TCan) => T): Otherwise<T> {
      let granted = false;
      const notify = () => {
        granted = true;
        notifyParent?.();
      };
      const intercepted = ((...args: never[]) => {
        const result = probe(...args);
        if (result) {
          notify();
        }
        return result;
      }) as CanWithNotify<TCan>;
      intercepted[notifyParentKey] = notify;

      const outcome = renderer(intercepted);

      return {
        otherwise<U>(fallback: U): T | U {
          return granted ? outcome : fallback;
        }
      };
    }
  };
}

function createIfCan(notifyParent?: () => void): IfCanFn {
  function ifCanLocal<TCan extends Probe>(can: TCan): IfCanChain<TCan> {
    return createChain(can, notifyParent);
  }

  function ready<TCanRd extends (...args: never[]) => RemoteData<boolean>>(
    canRd: TCanRd
  ): IfCanChain<(...args: Parameters<TCanRd>) => boolean> {
    const unwrapped = (...args: Parameters<TCanRd>) =>
      rd.getOrElse(canRd(...args), false);
    return createChain(unwrapped as Probe, notifyParent) as IfCanChain<
      (...args: Parameters<TCanRd>) => boolean
    >;
  }

  function union(): IfCanChain<() => boolean> {
    return ifCanLocal(() => false);
  }

  function childOf(parentCan: Probe): IfCanFn {
    return createIfCan(getNotify(parentCan) ?? notifyParent);
  }

  ifCanLocal.ready = ready;
  ifCanLocal.union = union;
  ifCanLocal.childOf = childOf;

  return ifCanLocal;
}

/**
 * Observe a boolean probe (typically `can`) while building UI, the way React
 * Hook Form observes `register` during render.
 *
 * The render callback receives a hijacked probe with the same signature. After
 * it returns, `otherwise` keeps the outcome only if the probe returned `true`
 * at least once; otherwise the fallback is used.
 *
 * Nested `ifCan` wrappers compose when they share the same intercepted `can`.
 * For a different probe under an observe-only parent, use
 * {@link ifCan.union} and {@link ifCan.childOf}.
 *
 * Prefer `ifCan.ready(useCan())` when the probe returns RemoteData per call
 * (AccessService.useCan). Pending / error → denied for that call.
 *
 * @example
 * ```tsx
 * {ifCan
 *   .ready(can)
 *   .render(can => (
 *     <DropdownMenu>
 *       {can({ action: Capability.PRODUCTION_CREATE }) && (
 *         <DropdownMenuItem>Production</DropdownMenuItem>
 *       )}
 *     </DropdownMenu>
 *   ))
 *   .otherwise(null)}
 * ```
 */
const ifCan = createIfCan();

export { ifCan };
